"use client";

import ChevronDown from "@/components/icons/ChevronDown";
import { useEmblaNavigation } from "@/hooks/useEmblaNavigation";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import type { LinkReturn } from "@frend-digital/storyblok";
import { clsx } from "@frend-digital/ui";
import { useEmblaCarousel } from "@frend-digital/ui/embla";
import type { EmblaCarouselType } from "embla-carousel";
import {
  type ComponentProps,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import styles from "./Carousel.module.css";

const arrowLeft = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M22.5 12H1.5" stroke="#161616" strokeMiterlimit="10" />
    <path
      d="M8.25 5.25L1.5 12L8.25 18.75"
      stroke="#161616"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
  </svg>
);
const arrowRight = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: "rotate(180deg)",
    }}
  >
    <path d="M22.5 12H1.5" stroke="#161616" strokeMiterlimit="10" />
    <path
      d="M8.25 5.25L1.5 12L8.25 18.75"
      stroke="#161616"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
  </svg>
);

export interface CarouselProps
  extends Omit<ComponentProps<"div">, "className" | "link" | "ref" | "title"> {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  ref?: RefObject<EmblaCarouselType | undefined>;
  showControls?: boolean;
  activeScroll?: boolean;
  title?: string;
  link?: LinkReturn;
  linkText?: string;
  wrapperClassName?: string;
  titleStyle?: string;
}

export const CartCarousel = ({
  children,
  className,
  containerClassName,
  ref: refProp,
  showControls,
  activeScroll = false,
  title,
  link,
  linkText,
  wrapperClassName,
  titleStyle,
  ...rest
}: CarouselProps) => {
  const t = useTranslations();
  const indicatorRef = useRef<HTMLDivElement>(null!);

  const [ref, emblaAPI] = useEmblaCarousel({
    loop: false,
    align: "start",
    duration: 25, // Increase duration for smoother transitions (was 1)
    dragFree: activeScroll,
  });

  // Improved scroll sync with smoother calculations
  const syncScroll = useCallback((api: EmblaCarouselType) => {
    try {
      const progress = api.scrollProgress();
      const container = indicatorRef.current?.parentElement;
      if (!container) return;

      // Use getBoundingClientRect for more accurate measurements
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;

      // Calculate indicator width (1/4 of container for 4 slides)
      const indicatorWidth = containerWidth / 4;
      const maxPosition = containerWidth - indicatorWidth;

      // Use smoother positioning without Math.floor for better precision
      const position = progress * maxPosition;

      // Apply transform with hardware acceleration
      indicatorRef.current.style.transform = `translate3d(${position}px, 0, 0)`;
    } catch (error) {
      console.warn("Scroll sync error:", error);
    }
  }, []);
  const handleResize = useCallback(() => {
    if (emblaAPI?.current) {
      syncScroll(emblaAPI.current);
    }
  }, [emblaAPI, syncScroll]);

  useEffect(() => {
    const api = emblaAPI?.current;
    if (!api) return;

    // Bind scroll event
    api.on("scroll", syncScroll);

    // Bind resize event for responsive updates
    api.on("resize", handleResize);

    // Initial sync
    syncScroll(api);

    return () => {
      api.off("scroll", syncScroll);
      api.off("resize", handleResize);
    };
  }, [emblaAPI, syncScroll, handleResize]);

  useImperativeHandle(refProp, () => emblaAPI.current);
  const { nextDisabled, onNextClick, onPrevClick, prevDisabled } =
    useEmblaNavigation(emblaAPI);
  return (
    <div className={clsx(styles.root, wrapperClassName)} {...rest}>
      {(title || (linkText && link) || showControls) && (
        <div className={clsx(styles.header)}>
          <div className={styles.headerInner}>
            {title && <h2 className={styles.title}>{title}</h2>}
            {linkText && link && (
              <Link href={link?.href} className={styles.link}>
                {linkText}
                <span className={styles.chevron}>
                  <ChevronDown />
                </span>
              </Link>
            )}
          </div>
        </div>
      )}
      <div className={clsx(styles.sliderRoot, className)} ref={ref}>
        <div className={clsx(styles.container, containerClassName)}>
          {children}
        </div>
      </div>
      <div className={styles.controlsContainer}>
        <ScrollIndicator ref={indicatorRef} />
        {showControls && (
          <div className={styles.controls}>
            <div className={styles.buttons}>
              <button
                type="button"
                aria-label={t("Previous")}
                onClick={onPrevClick}
                disabled={prevDisabled}
              >
                {arrowLeft}
              </button>
              <button
                type="button"
                aria-label={t("Next")}
                onClick={onNextClick}
                disabled={nextDisabled}
              >
                {arrowRight}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const ScrollIndicator = ({ ref }: { ref?: RefObject<HTMLDivElement> }) => {
  return (
    <div className={styles.swipeWrapper}>
      <div className={styles.swipeContainer}>
        <div className={styles.swipeBackground} />
        <div
          ref={ref}
          className={styles.swipeIndicator}
          /*     style={{
            width: `calc(100% / 4)`,
          }} */
        />
      </div>
    </div>
  );
};
